import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  DIRECTORY_KIND,
  DIRECTORY_TAG,
  parseDirectoryListing,
} from '@/lib/directoryListing';
import { BLOCKED_PUBKEYS } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';
import type { Merchant } from '@/lib/merchants';

/**
 * Fetch BCHNostr *directory* listings (kind 30023, tagged `bch-merchant`) and
 * map them into the app's `Merchant` shape.
 *
 * These are the lightweight paid listings created through BCHNostr's
 * "Add a listing" form — distinct from kind 30499 professional profiles. They
 * were previously not imported, so businesses that used that form never showed
 * up on BCH Local. They are addressable, so we keep only the latest event per
 * pubkey+d, then collapse to newest per pubkey for display.
 */
export function useDirectoryListings() {
  return useQuery({
    queryKey: ['nostr', 'merchants', 'bchnostr-directory'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{ kinds: [DIRECTORY_KIND], '#t': [DIRECTORY_TAG], limit: 500 }],
        { signal: discoverySignal(c.signal) },
      );

      // Keep the latest event per pubkey (a pubkey may have several listings;
      // we surface their most recent one so the map/list isn't cluttered with
      // stale duplicates).
      const latestByPubkey = new Map<string, (typeof events)[number]>();
      for (const event of events) {
        if (BLOCKED_PUBKEYS.has(event.pubkey)) continue;
        const existing = latestByPubkey.get(event.pubkey);
        if (!existing || event.created_at > existing.created_at) {
          latestByPubkey.set(event.pubkey, event);
        }
      }

      const merchants: Merchant[] = [];
      for (const event of latestByPubkey.values()) {
        const merchant = parseDirectoryListing(event);
        if (merchant) {
          merchants.push(merchant);
        }
      }

      return merchants;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
    // Union fresh results with the previous set so a temporarily-incomplete
    // relay response never removes known listings.
    structuralSharing: (prev, next) =>
      unionDirectory(prev as Merchant[] | undefined, next as Merchant[]),
  });
}

function unionDirectory(prev: Merchant[] | undefined, next: Merchant[]): Merchant[] {
  if (!prev || prev.length === 0) return next;

  const byPubkey = new Map<string, Merchant>();
  for (const m of prev) byPubkey.set(m.pubkey, m);
  for (const m of next) {
    const existing = byPubkey.get(m.pubkey);
    if (!existing || m.event.created_at >= existing.event.created_at) {
      byPubkey.set(m.pubkey, m);
    }
  }
  return [...byPubkey.values()];
}
